import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PlanningList,
  CreatePlanningListInput,
  UpdatePlanningListInput,
  ClonePlanningListInput,
  SavePlanningListAsTemplateInput,
  CreatePlanningItemInput,
  UpdatePlanningItemInput,
  CreatePlanningTodoInput,
  UpdatePlanningTodoInput,
  PlanningStatus,
  CreatePlanningStatusInput,
  UpdatePlanningStatusInput,
} from '@peles/shared';
import * as planningApi from '../api/planning';

// ─── Planning Lists ───────────────────────────────────────────────────────────

export function usePlanningLists(filters?: {
  eventId?: string;
  isTemplate?: boolean;
  eventTypeId?: string;
}) {
  return useQuery<PlanningList[]>({
    queryKey: ['planning-lists', filters ?? 'all'],
    queryFn: () => planningApi.getPlanningLists(filters),
  });
}

export function usePlanningList(id: string) {
  return useQuery<PlanningList>({
    queryKey: ['planning-lists', 'detail', id],
    queryFn: () => planningApi.getPlanningList(id),
    enabled: !!id,
  });
}

export function usePlanningTemplates(filters?: {
  eventTypeId?: string;
  includeArchived?: boolean;
}) {
  return useQuery<PlanningList[]>({
    queryKey: ['planning-templates', filters ?? 'all'],
    queryFn: () => planningApi.getPlanningTemplates(filters),
  });
}

export function useCreatePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlanningListInput) => planningApi.createPlanningList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useUpdatePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePlanningListInput }) =>
      planningApi.updatePlanningList(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useDeletePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => planningApi.deletePlanningList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useClonePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ClonePlanningListInput & { id: string }) =>
      planningApi.clonePlanningList(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useSavePlanningListAsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SavePlanningListAsTemplateInput & { id: string }) =>
      planningApi.savePlanningListAsTemplate(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

export function useArchivePlanningList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => planningApi.archivePlanningList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
      queryClient.invalidateQueries({ queryKey: ['planning-templates'] });
    },
  });
}

// ─── Planning Items ───────────────────────────────────────────────────────────

export function useAddPlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, ...input }: CreatePlanningItemInput & { listId: string }) =>
      planningApi.addPlanningItem(listId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useUpdatePlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: UpdatePlanningItemInput }) =>
      planningApi.updatePlanningItem(itemId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useDeletePlanningItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => planningApi.deletePlanningItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

// ─── Planning Todos ───────────────────────────────────────────────────────────

export function useAddPlanningTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, ...input }: CreatePlanningTodoInput & { itemId: string }) =>
      planningApi.addPlanningTodo(itemId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useUpdatePlanningTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ todoId, updates }: { todoId: string; updates: UpdatePlanningTodoInput }) =>
      planningApi.updatePlanningTodo(todoId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useDeletePlanningTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (todoId: string) => planningApi.deletePlanningTodo(todoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function usePlanningTodosForCalendar(year: number, month: number) {
  const dateFrom = new Date(year, month, 1).toISOString().slice(0, 10);
  const dateTo = new Date(year, month + 1, 0).toISOString().slice(0, 10);
  return useQuery({
    queryKey: ['planning-todos-calendar', year, month],
    queryFn: () => planningApi.getPlanningTodosForCalendar({ dateFrom, dateTo }),
  });
}

export function usePlanningTodosForDateRange(dateFrom: string, dateTo: string, enabled = true) {
  return useQuery({
    queryKey: ['planning-todos-calendar-range', dateFrom, dateTo],
    queryFn: () => planningApi.getPlanningTodosForCalendar({ dateFrom, dateTo }),
    enabled,
  });
}

// ─── Planning Statuses (Settings) ───────────────────────────────────────────────

export function usePlanningStatuses() {
  return useQuery<PlanningStatus[]>({
    queryKey: ['settings', 'planningStatuses'],
    queryFn: () => planningApi.getPlanningStatuses(),
  });
}

export function useCreatePlanningStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlanningStatusInput) => planningApi.createPlanningStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'planningStatuses'] });
    },
  });
}

export function useUpdatePlanningStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePlanningStatusInput }) =>
      planningApi.updatePlanningStatus(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'planningStatuses'] });
    },
  });
}
